// Stearns thank-you `.thank-you-summary` order block: the delivery + products
// sections (grouped lines, bundle callouts), the order-offer islands, and the
// `.thank-you-details` Order Summary panel (print button, Bill to / Payment
// columns with split-pay amounts and the pay-later notice, and the itemized
// breakdown). Consumes the typed OrderDetailData serialization and reproduces
// the legacy oscar/checkout/partials markup verbatim -- distinct from the
// shared OrderDetail `.details` layout (different classes:
// thank-you-progress-bar steps with the "Step completed" screen-reader spans,
// strike/coral price spans, and the __breakdown summary rows).
import type { Immutable } from "immer";
import React from "react";

import type { templates } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { Template as OrderOffer } from "../../templates/OrderOfferTemplate";

type IData = Immutable<
    NonNullable<templates.ThankYouDocumentTemplate["order_detail"]>
>;
type ISection = IData["shipped_sections"][number];
type ILine = ISection["lines"][number];
type IProgressBar = ISection["progress_bar"];
type IShippingTo = ISection["shipping_to"];
type IOffer = IData["offers"][number];

const ShippingTo = (
    props: Immutable<{ shippingTo: IShippingTo }>,
): React.JSX.Element => {
    const { shippingTo } = props;
    return (
        <>
            <h2 className="delivery__results__title">{shippingTo.status}</h2>
            <p className="delivery__results__address">
                To: {shippingTo.name}
                <br />
                {shippingTo.line1},{" "}
                {shippingTo.line2 && <>{shippingTo.line2}, </>}
                {shippingTo.line4}, {shippingTo.state} {shippingTo.postcode}
            </p>
        </>
    );
};

const ProgressStep = (
    props: Immutable<{ checked: boolean; current: boolean; label: string }>,
): React.JSX.Element => (
    <li
        className={`thank-you-progress-bar__list-item${props.checked ? " is-checked" : ""}`}
    >
        {/* The legacy partial marks only the CURRENT bucket's step for screen
            readers. */}
        {props.current && (
            <span className="ada-screenreader-only">Step completed:</span>
        )}
        <span className="item__title">{props.label}</span>
    </li>
);

const ProgressBar = (
    props: Immutable<{ progressBar: IProgressBar }>,
): React.JSX.Element => {
    const { progressBar } = props;
    const threeStep = (step: "received" | "preparing" | "shipped") => (
        <ul className="thank-you-progress-bar__list">
            <ProgressStep
                checked
                current={step === "received"}
                label="Order Received"
            />
            <ProgressStep
                checked={step !== "received"}
                current={step === "preparing"}
                label="Preparing"
            />
            <ProgressStep
                checked={step === "shipped"}
                current={step === "shipped"}
                label="Shipped"
            />
        </ul>
    );
    return (
        <div className="thank-you-progress-bar">
            {progressBar.step === "received" && threeStep("received")}
            {progressBar.step === "preparing" && threeStep("preparing")}
            {progressBar.step === "shipped" && threeStep("shipped")}
            {progressBar.step === "error" && (
                <ul className="thank-you-progress-bar__list">
                    <li className="thank-you-progress-bar__list-item is-error">
                        <span className="item__title">
                            {progressBar.error_label}
                        </span>
                    </li>
                </ul>
            )}
        </div>
    );
};

const OrderLine = (props: Immutable<{ line: ILine }>): React.JSX.Element => {
    const { line } = props;
    const { price } = line;
    return (
        <div role="group" aria-label="product">
            <div className="section__left products__results">
                <div className="product__image">
                    <a href={line.product_link} title={line.link_title}>
                        <img
                            src={line.image_url}
                            alt={line.image_alt}
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                </div>
                <div className="product__details">
                    <p className="product__details__copy">
                        {line.title}
                        <br />
                        {line.attribute_summary}
                        <br />
                        Qty: {line.quantity}
                        <br />
                        {!line.is_bundled &&
                            (price.is_discounted ? (
                                <>
                                    <span className="product__details__copy--strike">
                                        {price.msrp_display}
                                    </span>{" "}
                                    <span className="product__details__copy--coral">
                                        {price.actual_display}
                                    </span>
                                </>
                            ) : (
                                <span>{price.actual_display}</span>
                            ))}
                        {!line.is_bundled && price.addon_display && (
                            <>
                                {" "}
                                <span>+{price.addon_display}</span>
                            </>
                        )}
                    </p>
                    {/* The legacy partial nested this div inside the <p>; a
                        browser auto-splits that into siblings, so render it as
                        a sibling here to match the real DOM (and avoid a
                        <div>-in-<p> hydration mismatch). */}
                    {line.is_bundled && (
                        <div className="order-detail__bundle-callout">
                            {line.bundle_callout}
                        </div>
                    )}
                </div>
            </div>
            <div className="section__right products__status">
                <p className="products__status__copy">
                    Item Status: {line.item_status}
                    <br />
                    Shipping Method: {line.shipping_method_label}
                    <br />
                    {line.tracking_number && (
                        <>Tracking Number: {line.tracking_number}</>
                    )}
                </p>
            </div>
        </div>
    );
};

const OrderSection = (
    props: Immutable<{ section: ISection }>,
): React.JSX.Element => {
    const { section } = props;
    return (
        <>
            <div className="section delivery">
                <div className="section__left delivery__results">
                    <ShippingTo shippingTo={section.shipping_to} />
                </div>
                <div className="section__right delivery__status">
                    <ProgressBar progressBar={section.progress_bar} />
                </div>
            </div>
            <div className="section products">
                {section.lines.map((line, i) => (
                    <OrderLine key={i} line={line} />
                ))}
            </div>
        </>
    );
};

const OrderOffers = (
    props: Immutable<{ offers: readonly IOffer[] }>,
): React.JSX.Element | null => {
    const { offers } = props;
    if (offers.length === 0) {
        return null;
    }
    return (
        <>
            {offers.map((offer, i) => (
                <div key={i} className="thank-you-order-offer l-capped-width">
                    <OrderOffer {...offer} />
                </div>
            ))}
        </>
    );
};

// The shared/_svg.html print icon exactly as the legacy include rendered it
// (src='print', title='Print Order', class='thank-you-details__print-icon').
const PrintIcon = (): React.JSX.Element => (
    <svg
        className="thank-you-details__print-icon"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        data-name="Layer 1"
        viewBox="0 0 91 88.66"
    >
        <title>Print Order</title>
        <path d="M75.67 38.83H61.34v-5.34h14.33zm2-9.34H59.34a2 2 0 0 0-2 2v9.34a2 2 0 0 0 2 2h18.33a2 2 0 0 0 2-2v-9.34a2 2 0 0 0-2-2M29.5 78.33h32a2 2 0 0 0 0-4h-32a2 2 0 0 0 0 4m0-10.67h32a2 2 0 0 0 0-4h-32a2 2 0 0 0 0 4m57.5-9a3 3 0 0 1-3 3h-8.67v-6.33h2.34a2 2 0 0 0 0-4H13.33a2 2 0 0 0 0 4h3.34v6.33H7a3 3 0 0 1-3-3V25a3 3 0 0 1 3-3h77a3 3 0 0 1 3 3zm-15.67 26H20.67V55.33h50.66zM11.06 4h68.88v14H11.06zM84 18h-.06V2a2 2 0 0 0-2-2H9.06a2 2 0 0 0-2 2v16H7a7 7 0 0 0-7 7v33.66a7 7 0 0 0 7 7h9.67v21a2 2 0 0 0 2 2h54.66a2 2 0 0 0 2-2v-21H84a7 7 0 0 0 7-7V25a7 7 0 0 0-7-7" />
    </svg>
);

export const OrderDetail = (
    props: Immutable<{ data: IData }>,
): React.JSX.Element => {
    const { data } = props;
    return (
        <div className="thank-you-summary">
            {data.shipped_sections.map((section, i) => (
                <OrderSection key={i} section={section} />
            ))}

            {/* The legacy template gates the unshipped section on the RAW
                order.unshipped_quantities, BEFORE bundle-parent suppression:
                when every unshipped line is a suppressed bundle parent the
                delivery/progress header still renders, with no product lines. */}
            {data.show_unshipped_section && (
                <OrderSection section={data.unshipped_section} />
            )}

            <OrderOffers offers={data.offers} />

            <div className="thank-you-details details">
                <div className="thank-you-details__column-header">
                    <h4 className="thank-you-details__title">Order Summary</h4>
                    {/* The legacy click handler lives on the wrapping div, not
                        the button. */}
                    <div
                        className="thank-you-details__print"
                        onClick={() => {
                            getWindow()?.print();
                        }}
                    >
                        <button className="thank-you-details__print-icon-container">
                            Print
                            <span className="ada-screenreader-only">
                                Print Order Details
                            </span>
                            <PrintIcon />
                        </button>
                    </div>
                </div>
                <div className="thank-you-details__columns">
                    <div className="thank-you-details__column">
                        <h2 className="thank-you-details__column__title">
                            Bill to
                        </h2>
                        <div className="thank-you-details__copy-block thank-you-details__address">
                            <div>{data.billing_name}</div>
                            <div>
                                {data.billing_line1}
                                {data.billing_line2 && (
                                    <>, {data.billing_line2}</>
                                )}
                                {data.billing_line3 && (
                                    <>, {data.billing_line3}</>
                                )}
                            </div>
                            <div>
                                {data.billing_line4}, {data.billing_state}{" "}
                                {data.billing_postcode}
                            </div>
                        </div>
                    </div>

                    <div className="thank-you-details__column">
                        <h2 className="thank-you-details__column__title">
                            Payment
                        </h2>
                        <div className="thank-you-details__copy-block thank-you-details__payment">
                            {data.cybersource_cards.map((card, i) => (
                                <div
                                    key={`cs-${i}`}
                                    className="thank-you-details__cc_payment"
                                >
                                    <span className="thank-you-details__card_type">
                                        {card.card_type_name}
                                    </span>{" "}
                                    ending in{" "}
                                    <span className="thank-you-details__card_mask">
                                        {card.masked_card_number}
                                    </span>
                                    {card.amount_display && (
                                        <span className="thank-you-details__amount">
                                            {card.amount_display}
                                        </span>
                                    )}
                                </div>
                            ))}
                            {data.financing_transfers.map((transfer, i) => (
                                <div
                                    key={`fin-${i}`}
                                    className="thank-you-details__financing_payment"
                                >
                                    <div className="thank-you-details__account">
                                        <span>
                                            Account:{" "}
                                            {transfer.masked_account_number}
                                        </span>
                                        {transfer.amount_display && (
                                            <span className="thank-you-details__amount">
                                                {transfer.amount_display}
                                            </span>
                                        )}
                                    </div>
                                    {transfer.disclosure && (
                                        <span className="thank-you-details__terms">
                                            Terms: {transfer.disclosure}
                                        </span>
                                    )}
                                </div>
                            ))}
                            {data.is_pay_later && (
                                <div className="thank-you-details__pay-later">
                                    <p>
                                        <em>
                                            Payment will be completed at a later
                                            time.
                                        </em>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="thank-you-details__breakdown thank-you-details__column">
                        <div className="thank-you-details__breakdown-row">
                            <div className="thank-you-details__breakdown-title">
                                Subtotal:
                            </div>
                            <div className="thank-you-details__breakdown-number">
                                {data.subtotal_display}
                            </div>
                        </div>

                        <div className="thank-you-details__breakdown-row">
                            <div className="thank-you-details__breakdown-title">
                                Shipping:
                            </div>
                            <div className="thank-you-details__breakdown-number">
                                {data.shipping_display}
                            </div>
                        </div>

                        {data.is_tax_known ? (
                            data.itemized_tax_groups.map((group, i) => (
                                <div
                                    key={i}
                                    className="thank-you-details__breakdown-row"
                                >
                                    <div className="thank-you-details__breakdown-title">
                                        {group.name}
                                    </div>
                                    <div className="thank-you-details__breakdown-number">
                                        {group.amount_display}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="thank-you-details__breakdown-row">
                                <div className="thank-you-details__breakdown-title">
                                    Tax:
                                </div>
                                <div className="thank-you-details__breakdown-number">
                                    Tax will be calculated upon shipment
                                </div>
                            </div>
                        )}

                        {/* The legacy row div renders even without a discount
                            (contents conditional, the row itself not). */}
                        <div className="thank-you-details__breakdown-row">
                            {data.has_discount && (
                                <>
                                    <div className="thank-you-details__breakdown-title">
                                        Discount:
                                    </div>
                                    <div className="thank-you-details__breakdown-number thank-you-details__breakdown-number--coral">
                                        {data.discount_display}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="thank-you-details__breakdown-row">
                            <div className="thank-you-details__breakdown-title">
                                Order Total:
                            </div>
                            <div className="thank-you-details__breakdown-number">
                                {data.total_display}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
